import { useNavigate } from 'react-router-dom';
import { useOfflinePdaZones } from '@/hooks/useOfflinePdaZones';
import ZoneCoverageBar from '@/components/field/ZoneCoverageBar';
import type { PdaZoneStatus } from '@/types/pda-zone';
import { ZONE_STATUS_CONFIG } from '@/types/pda-zone';
import { useState } from 'react';
import FaIcon from '@/components/common/FaIcon';
import Badge from '@/components/common/Badge';
import EmptyState from '@/components/common/EmptyState';
import { MapIcon } from '@/components/icons';
import LoadingState from '@/components/common/LoadingState';
import Button, { IconButton } from '@/components/common/Button';
import MetricValue from '@/components/common/MetricValue';

export default function FieldPdaZones() {
  const navigate = useNavigate();
  const { zoneSets, zones, isLoading, isOffline, pendingBreadcrumbs, loadZones, syncNow, refresh } = useOfflinePdaZones();
  const [expandedSet, setExpandedSet] = useState<string | null>(null);

  const toggleSet = (setId: string) => {
    if (expandedSet === setId) {
      setExpandedSet(null);
    } else {
      setExpandedSet(setId);
      loadZones(setId);
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading zones..." />;
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="heading-3">PDA Zones</h2>
          {isOffline && (
            <Badge variant="warning" size="sm">Offline</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {pendingBreadcrumbs > 0 && (
            <Button
              onClick={() => syncNow()}
              variant="ghost"
              size="sm"
              className="text-amber-600 dark:text-amber-400"
            >
              Sync (<MetricValue value={pendingBreadcrumbs} />)
            </Button>
          )}
          <IconButton icon={<FaIcon icon="arrows-rotate" className="h-4 w-4" />} label="Refresh zones" onClick={() => refresh()} variant="ghost" size="sm" />
          <Button
            onClick={() => navigate('/field/zones/setup')}
            variant="ghost"
            size="sm"
          >
            Setup
          </Button>
        </div>
      </div>

      {zoneSets.length === 0 ? (
        <EmptyState
          variant="operational"
          icon="map"
          title={isOffline ? 'No zones cached for offline use' : 'No zone sets created'}
          description={isOffline ? undefined : 'Create a zone set to start tracking assessments.'}
          actionLabel={isOffline ? undefined : 'Create Zone Set'}
          onAction={isOffline ? undefined : () => navigate('/field/zones/setup')}
        />
      ) : (
        <div className="space-y-2">
          {zoneSets.map((zs) => (
            <div key={zs.id} className="bg-surface-primary rounded-lg border border-theme-primary overflow-hidden">
              <button
                onClick={() => toggleSet(zs.id)}
                className="w-full text-left p-3 min-h-[44px] touch-manipulation active:bg-surface-secondary"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-theme-primary truncate">{zs.name}</p>
                    <p className="text-xs text-theme-tertiary mt-0.5">
                      <MetricValue value={zs.zone_count || 0} /> zones &middot; <MetricValue value={zs.zones_complete || 0} /> complete
                    </p>
                  </div>
                  <FaIcon icon="chevron-down" className={`h-5 w-5 text-theme-tertiary transition-transform ${expandedSet === zs.id ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {expandedSet === zs.id && (
                <div className="border-t border-theme-primary">
                  <div className="px-3 pt-2 pb-1">
                    <Button
                      onClick={() => navigate(`/field/zones/manage/${zs.id}`)}
                      variant="primary"
                      size="sm"
                      icon={<MapIcon className="h-3.5 w-3.5" />}
                    >
                      Manage Zones
                    </Button>
                  </div>
                  {!zones[zs.id] ? (
                    <div className="p-3 text-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto" />
                    </div>
                  ) : zones[zs.id].length === 0 ? (
                    <p className="p-3 text-xs text-theme-tertiary text-center">No zones generated yet</p>
                  ) : (
                    <div className="divide-y divide-theme-primary">
                      {zones[zs.id].map((zone) => {
                        const statusCfg = ZONE_STATUS_CONFIG[zone.status as PdaZoneStatus];
                        return (
                          <button
                            key={zone.id}
                            onClick={() => navigate(`/field/zones/${zone.id}/map`)}
                            className="w-full text-left px-3 py-2.5 min-h-[44px] touch-manipulation active:bg-surface-secondary"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-theme-primary">
                                  {zone.name || `Zone ${zone.zone_number}`}
                                </p>
                                {zone.assigned_to_name && (
                                  <p className="text-xs text-theme-tertiary mt-0.5">{zone.assigned_to_name}</p>
                                )}
                                <div className="mt-1.5">
                                  <ZoneCoverageBar
                                    percent={zone.coverage_percent}
                                    cellsVisited={zone.grid_cells_visited}
                                    cellsTotal={zone.grid_cells_total}
                                    compact
                                  />
                                </div>
                              </div>
                              {statusCfg && (
                                <Badge variant={statusCfg.variant} size="sm">
                                  {statusCfg.label}
                                </Badge>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
